import { useState } from "react";
import { Card, CardContent } from "@/shared/ui/card";
import { Input } from "@/shared/ui/input";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";
import { Search, Building2, ChevronRight, MapPin, Tags } from "lucide-react";
import type { ProjectWithProperties } from "@/features/projects/services/projectsAdapter";
import { ManageCategoryDescriptionsModal } from "@/features/assets/components/ManageCategoryDescriptionsModal";

interface ProjectSelectorProps {
  projects: ProjectWithProperties[];
  loading: boolean;
  onSelect: (project: ProjectWithProperties) => void;
}

export function ProjectSelector({ projects, loading, onSelect }: ProjectSelectorProps) {
  const [search, setSearch] = useState("");
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const filtered = projects
    .filter((p) => p.name !== "Unassigned")
    .filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full max-w-md" />
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Search + actions */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="project-search"
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button variant="outline" onClick={() => setCategoriesOpen(true)} className="shrink-0">
          <Tags className="h-4 w-4 mr-1" />
          Manage Asset Categories Subtypes
        </Button>
      </div>

      <ManageCategoryDescriptionsModal open={categoriesOpen} onOpenChange={setCategoriesOpen} />

      {/* Grid */}
      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {filtered.map((project, i) => (
          <button
            key={project.id}
            onClick={() => onSelect(project)}
            className="group text-left animate-fade-in"
            style={{ animationDelay: `${i * 60}ms` }}
          >
            <Card className="h-full transition-all duration-300 hover:shadow-lg hover:scale-[1.02] group-hover:border-primary/40 cursor-pointer">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="h-11 w-11 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
                  {project.logo_url ? (
                    <img
                      src={project.logo_url}
                      alt={`${project.name} logo`}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <Building2 className="h-5 w-5 text-primary" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{project.name}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                    <MapPin className="h-3 w-3" />
                    {project.properties.length} properties
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
              </CardContent>
            </Card>
          </button>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">
            {search ? "No projects match your search." : "No projects available."}
          </p>
        </div>
      )}
    </div>
  );
}
